import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import MBOTColorLogo from "../../assets/authentication/MBOTColorLogo.png";
import Warning from "../../assets/authentication/Warning.png";
import { activateAccount, resendEmail } from "../../redux/slices/authSlice";
import { ACTIVATE_ACCOUNT_RESPONSE_CODES } from "../../data/Constants";
import "./EmailVerification.css"

const ActivateAccount = () => {
  const loading = useSelector((state) => state.auth.loading);
  const error = useSelector((state) => state.auth.error);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(activateAccount({ token: params?.token, navigate }));
  }, [params, dispatch, navigate]);

  return (
    // Container for the activate account screen
    <div className="bg-blue-10 email-verification-outer-container center-alignment">
      {/* White background container for the activate account content */}
      <div className="email-verification-inner-container m-2 center-alignment flex-column rounded">
        {/* MBOTColorLogo */}
        <img src={MBOTColorLogo} alt="MBOT Logo" className="mbot-color-logo" />
        {loading ? (
          // Show loader if loading
          <section className="text-center my-5 center-alignment gap-2">
            <span className="primary-loader mt-2"></span>
            <span className="fs-20">
              {t("COMMON.VERIFYING.YOUR.ACCOUNT")}...
            </span>
          </section>
        ) : (
          // Show activation status component if not loading
          <ActivateAccountStatus statusCode={error?.code} />
        )}
      </div>
    </div>
  );
};

export default ActivateAccount;

const ActivateAccountStatus = ({ statusCode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const resendLoading = useSelector((state) => state.auth.resendLoading);
  const emailSent = useSelector((state) => state.auth.emailSent);

  let statusMessage,
    resendLink = false;
  switch (statusCode) {
    case ACTIVATE_ACCOUNT_RESPONSE_CODES.LINK_EXPIRED:
      statusMessage = "COMMON.LINK.EXPIRED";
      resendLink = true;
      break;
    case ACTIVATE_ACCOUNT_RESPONSE_CODES.INVALID_LINK:
      statusMessage = "COMMON.LINK.INVALID";
      break;
    case ACTIVATE_ACCOUNT_RESPONSE_CODES.ACCOUNT_NOT_FOUND:
      statusMessage = "COMMON.ACCOUNT.NOT.FOUND";
      break;
    default:
      statusMessage = "";
  }

  const handleResendEmail = () => {
    const data = { token: params?.token };
    dispatch(resendEmail({ data, navigate }));
  };

  return (
    <>
      <img
        src={Warning}
        alt={t(statusMessage)}
        className="img-fluid auth-page-header-img"
      />

      {/* Section containing text content for activate account */}
      <section className="text-center">
        {/* Header section with activate account title */}
        <header>
          {
            <h5 className="text-primary-sapphire fw-600 fs-22">
              {emailSent
                ? t("COMMON.EMAIL.SENT")
                : t("COMMON.VERIFICATION.FAILED")}
              !
            </h5>
          }
          {/* Subtitle with the user's name and thank you message */}
          <h6 className="fw-500 fs-18 text-gray-250">{t(statusMessage)}</h6>
        </header>

        {/* Footer to resend link to activate account in case of link expiration */}
        {resendLink && (
          <footer className="footer">
            <button
              className="fs-20 fw-500 text-primary-sapphire auth-resend-btn"
              onClick={handleResendEmail}
              disabled={resendLoading}
            >
              {resendLoading ? (
                <span className="secondary-loader"></span>
              ) : (
                t("COMMON.RESEND.LINK")
              )}
            </button>
          </footer>
        )}
      </section>
    </>
  );
};
